"use client"

import { useState } from "react"
import api from "../../services/api"

export default function ForgotPassword() {
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage("")
    setError("")

    try {
      const res = await api.post("/auth/forgot-password", { email })
      if (!res.data?.success) {
        setError(res.data?.message || "Failed to send reset email")
      } else {
        setMessage("Password reset link sent to your email")
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Error occurred")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .floating-orb {
          animation: float 6s ease-in-out infinite;
        }
        .spinner-border-custom {
          width: 1rem;
          height: 1rem;
          border-width: 2px;
          animation: spin 0.75s linear infinite;
        }
      `}</style>

      <div
        className="d-flex justify-content-center align-items-center vh-100 position-relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #F3F4F6 100%)",
        }}
      >
        <div
          className="position-absolute floating-orb"
          style={{
            width: "300px",
            height: "300px",
            background: "radial-gradient(circle, rgba(70, 94, 229, 0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            top: "-100px",
            left: "-100px",
            filter: "blur(40px)",
          }}
        />
        <div
          className="position-absolute floating-orb"
          style={{
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(34, 197, 94, 0.06) 0%, transparent 70%)",
            borderRadius: "50%",
            bottom: "-150px",
            right: "-150px",
            filter: "blur(50px)",
            animationDelay: "2s",
          }}
        />

        <div
          className="card shadow-lg p-4 position-relative"
          style={{
            maxWidth: "450px",
            width: "100%",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "16px",
          }}
        >
          <div className="text-center mb-4">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{
                width: "64px",
                height: "64px",
                background: "linear-gradient(135deg, #1130df 0%, #171b23 100%)",
                borderRadius: "16px",
                boxShadow: "0 8px 16px rgba(79, 70, 229, 0.3)",
              }}
            >
              <svg
                width="32"
                height="32"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h2 className="fw-bold mb-2" style={{ color: "#1F2937" }}>
              Forgot Password?
            </h2>
            <p className="mb-0" style={{ color: "#6B7280", fontSize: "0.95rem" }}>
              No worries, we'll send you reset instructions
            </p>
          </div>

          {message && (
            <div
              className="alert d-flex align-items-center mb-3"
              style={{
                backgroundColor: "rgba(34, 197, 94, 0.1)",
                border: "1px solid rgba(34, 197, 94, 0.3)",
                color: "#16A34A",
                borderRadius: "12px",
              }}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20" className="me-2">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              {message}
            </div>
          )}

          {error && (
            <div
              className="alert d-flex align-items-center mb-3"
              style={{
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#DC2626",
                borderRadius: "12px",
              }}
            >
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20" className="me-2">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4 position-relative">
              <div
                className="position-absolute"
                style={{ left: "16px", top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="#6B7280"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <input
                type="email"
                className="form-control"
                id="forgotEmail"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  paddingLeft: "48px",
                  height: "52px",
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E5E7EB",
                  color: "#1F2937",
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#0d25d8"
                  e.target.style.boxShadow = "0 0 0 3px rgba(79, 70, 229, 0.1)"
                  e.target.style.backgroundColor = "#FFFFFF"
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E5E7EB"
                  e.target.style.boxShadow = "none"
                  e.target.style.backgroundColor = "#F9FAFB"
                }}
              />
            </div>

            <button
              type="submit"
              className="btn w-100 fw-semibold position-relative"
              disabled={loading}
              style={{
                height: "52px",
                background: loading ? "#9CA3AF" : "linear-gradient(135deg, #1130df 0%, #171b23 100%)",
                border: "none",
                color: "white",
                borderRadius: "12px",
                fontSize: "1rem",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 12px rgba(70, 94, 229, 0.1)",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = "translateY(-2px)"
                  e.target.style.boxShadow = "0 6px 20px rgba(53, 80, 232, 0.5)"
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)"
                e.target.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.4)"
              }}
            >
              {loading ? (
                <span className="d-flex align-items-center justify-content-center">
                  <span
                    className="spinner-border-custom border-2 me-2"
                    style={{
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      borderTopColor: "white",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  Sending...
                </span>
              ) : (
                "Send Reset Link"
              )}
            </button>
          </form>

          <div className="text-center mt-4">
            <a
              href="/login"
              className="text-decoration-none"
              style={{
                color: "#6B7280",
                fontSize: "0.9rem",
                transition: "color 0.3s ease",
              }}
              onMouseEnter={(e) => (e.target.style.color = "#0900b9da")}
              onMouseLeave={(e) => (e.target.style.color = "#6B7280")}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                style={{ marginBottom: "2px", marginRight: "6px" }}
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to login
            </a>
          </div>
        </div>
      </div>
    </>
  )
}
